// ★ Find missing players (Flow 3: UC-10 → UC-11 → UC-12 → UC-13) — the product's
// core differentiator and the demo centerpiece. The organizer sees a *ranked,
// explained* candidate list, multi-selects, sends invites, and then watches the
// roster fill in real time as simulated players accept/decline.
//
// This screen owns its own live DOM updates (subscribing to invite/game events)
// instead of relying on the app-wide full re-render, so the fill animation and
// selection state survive incoming events. Subscriptions are torn down on unmount.

import $ from 'jquery';
import type { Id, User } from '@shared/models';
import type { GameDetail, GroupSummary, RankedCandidate } from '@shared/dto';
import type { Screen } from './types';
import { api } from '../api';
import { onCleanup } from '../router';
import { avatarHtml, esc } from '../ui/dom';
import { whenLong } from '../ui/format';
import { emptyHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';

type InviteState = 'pending' | 'accepted' | 'declined';

const STATUS_LABEL: Record<InviteState, string> = {
  pending: '⏳ Bekleniyor',
  accepted: '✅ Kabul etti',
  declined: '✖ Reddetti',
};

function scoreTier(score: number): string {
  if (score >= 75) return 'score--high';
  if (score >= 50) return 'score--mid';
  return 'score--low';
}

function candidateHtml(c: RankedCandidate): string {
  const reasons = c.reasons.map((r) => `<span class="rchip">${esc(r)}</span>`).join('');
  return `
  <div class="candidate" data-player="${esc(c.player.id)}">
    <div class="candidate__check" aria-hidden="true"></div>
    <div class="score-ring ${scoreTier(c.score)}" style="--pct:${c.score}">
      <span class="score-ring__num">${c.score}</span>
    </div>
    <div class="candidate__main">
      <div class="candidate__head">
        ${avatarHtml(c.player.name, c.player.avatarUrl, 32)}
        <span class="candidate__name">${esc(c.player.name)}</span>
      </div>
      <div class="candidate__reasons">${reasons}</div>
    </div>
  </div>`;
}

function inviteRowHtml(player: User, status: InviteState): string {
  return `
  <div class="invite-row" data-invite-player="${esc(player.id)}">
    ${avatarHtml(player.name, player.avatarUrl, 32)}
    <span class="roster__name">${esc(player.name)}</span>
    <span class="invite-row__status status--${status}">${STATUS_LABEL[status]}</span>
  </div>`;
}

export const FindPlayersScreen: Screen = async (body, params) => {
  body.html(loadingHtml());

  const gameId = params.id;
  let detail: GameDetail;
  let candidates: RankedCandidate[];
  let groups: GroupSummary[];
  try {
    [detail, candidates, groups] = await Promise.all([
      api.call('games.get', { gameId }),
      api.call('matching.findPlayers', { gameId }),
      api.call('groups.mine', undefined),
    ]);
  } catch (err) {
    body.html(emptyHtml('⚠️', err instanceof Error ? err.message : String(err)));
    return;
  }

  if (!detail.viewerIsOrganizer) {
    body.html(emptyHtml('🔒', 'Sadece organizatör oyuncu davet edebilir.'));
    return;
  }
  if (detail.game.status === 'cancelled') {
    body.html(emptyHtml('❌', 'Bu oyun iptal edilmiş.'));
    return;
  }

  const capacity = detail.game.capacity;
  let filled = detail.game.participantIds.length;
  const selected = new Set<Id>();
  const invited = new Map<Id, { player: User; status: InviteState }>();
  const byId = new Map<Id, User>(candidates.map((c) => [c.player.id, c.player]));

  // Group chips are a bulk-select shortcut: a group's "eligible" members are the
  // ones still invitable right now (i.e. still in the ranked candidate pool).
  const groupEligible = new Map<Id, Id[]>();
  const groupChips = groups
    .map((g) => {
      const eligible = g.members.filter((m) => byId.has(m.id)).map((m) => m.id);
      if (eligible.length === 0) return '';
      groupEligible.set(g.id, eligible);
      return `<button class="chip" data-group="${esc(g.id)}">${esc(g.name)} · ${eligible.length}</button>`;
    })
    .join('');

  const listHtml = candidates.length
    ? candidates.map(candidateHtml).join('')
    : emptyHtml(
        '🔒',
        'Davet edebileceğin arkadaş yok. Gizlilik için sadece arkadaşların ve gruplarındaki kişiler önerilir — arkadaş ekle, bir gruba katıl ya da oyunu herkese açık yap.',
      );

  body.html(`
    <div class="find">
      <div class="find__ctx">
        <div class="find__court">${esc(detail.court.name)}</div>
        <div class="find__when">${esc(whenLong(detail.game.startsAt))}</div>
      </div>

      <div class="find__progress" id="find-progress">${progressHtml(filled, capacity)}</div>

      ${groupChips ? `<div class="section-title">Gruplarından davet et</div><div class="find__groups">${groupChips}</div>` : ''}

      <div class="section-title" id="invite-title" ${invited.size ? '' : 'hidden'}>Davetler</div>
      <div class="roster" id="invite-list"></div>

      ${candidates.length ? '<div class="section-title">Önerilen oyuncular (arkadaşların)</div>' : ''}
      <div id="candidate-list">${listHtml}</div>
    </div>

    <div class="findbar">
      <button class="btn btn--ghost" id="btn-ff" title="Demo: yanıtları hızlandır">⏩</button>
      <button class="btn btn--primary btn--block" id="btn-invite" disabled>Oyuncu seç</button>
    </div>
  `);

  function progressHtml(f: number, cap: number): string {
    const pct = Math.min(100, Math.round((f / cap) * 100));
    const left = Math.max(0, cap - f);
    const label = left === 0 ? 'Kadro tamamlandı 🎉' : `${left} kişi aranıyor`;
    return `
      <div class="find__count">${f}/${cap} · <b>${label}</b></div>
      <div class="progress"><div class="progress__bar" style="width:${pct}%"></div></div>`;
  }

  function updateProgress(): void {
    $('#find-progress').html(progressHtml(filled, capacity));
    if (filled >= capacity) {
      $('#btn-invite').prop('disabled', true).text('Kadro tamamlandı 🎉');
      body.find('.candidate').css('opacity', 0.4).css('pointer-events', 'none');
    }
  }

  function refreshInviteButton(): void {
    const $btn = $('#btn-invite');
    if (filled >= capacity) return;
    if (selected.size === 0) {
      $btn.prop('disabled', true).text('Oyuncu seç');
    } else {
      $btn.prop('disabled', false).text(`⚡ Davet gönder (${selected.size})`);
    }
  }

  function showInvite(player: User, status: InviteState): void {
    $('#invite-title').prop('hidden', false);
    const existing = body.find(`[data-invite-player="${player.id}"]`);
    if (existing.length) {
      existing.replaceWith(inviteRowHtml(player, status));
    } else {
      $('#invite-list').append(inviteRowHtml(player, status));
    }
  }

  function setSelected(id: Id, on: boolean): void {
    if (on) selected.add(id);
    else selected.delete(id);
    body.find(`.candidate[data-player="${id}"]`).toggleClass('is-selected', on);
  }

  // --- Interactions -------------------------------------------------------
  body.on('click', '.candidate', function () {
    if (filled >= capacity) return;
    const id = $(this).data('player') as Id;
    setSelected(id, !selected.has(id));
    refreshInviteButton();
  });

  // Group chip: toggle-select all of the group's still-invitable members.
  body.on('click', '.chip[data-group]', function () {
    if (filled >= capacity) return;
    const gid = $(this).data('group') as Id;
    const ids = (groupEligible.get(gid) ?? []).filter((id) => !invited.has(id));
    if (ids.length === 0) return;
    const allOn = ids.every((id) => selected.has(id));
    for (const id of ids) setSelected(id, !allOn);
    $(this).toggleClass('is-on', !allOn);
    refreshInviteButton();
  });

  $('#btn-invite').on('click', async () => {
    const ids = [...selected];
    if (ids.length === 0) return;

    // Optimistic: move selected candidates into the invite list as pending.
    for (const id of ids) {
      const player = byId.get(id);
      if (!player) continue;
      invited.set(id, { player, status: 'pending' });
      showInvite(player, 'pending');
      body.find(`.candidate[data-player="${id}"]`).remove();
    }
    selected.clear();
    refreshInviteButton();

    try {
      await api.call('invites.send', { gameId, playerIds: ids });
      toast(`${ids.length} davet gönderildi ✉️`, 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  $('#btn-ff').on('click', async () => {
    await api.call('demo.fastForward', { ms: 10_000 });
  });

  // --- Live world updates -------------------------------------------------
  const offInvite = api.on('invite-updated', ({ invite, gameId: gid }) => {
    if (gid !== gameId) return;
    const rec = invited.get(invite.playerId);
    const player = rec?.player ?? byId.get(invite.playerId);
    if (!player) return;
    const status: InviteState =
      invite.status === 'accepted' ? 'accepted' : invite.status === 'declined' ? 'declined' : 'pending';
    invited.set(invite.playerId, { player, status });
    showInvite(player, status);
  });

  const offGame = api.on('game-updated', ({ gameId: gid }) => {
    if (gid !== gameId) return;
    // Re-read the authoritative roster size so the bar reflects reality.
    void api.call('games.get', { gameId }).then((d) => {
      filled = d.game.participantIds.length;
      updateProgress();
    });
  });

  onCleanup(offInvite);
  onCleanup(offGame);
};
